/**
 * FP3-029A — Admin Student Assignment management.
 *
 * Lists org-scoped students with their active provider assignments,
 * lets admins assign or reassign students to providers without
 * leaving the page. Super admin must pick an organization; org/admin
 * users are pinned to their own org.
 *
 * NOT a shared-collaboration surface — assignment only. Edits to
 * goals / sessions / evidence still happen inside each provider's
 * own workspace (FP3-031 will layer collaboration on top).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  GraduationCap,
  X,
  Plus,
  Buildings,
  WarningCircle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import {
  invalidateAfterMutation,
  useStudentAssignments,
  invalidateStudentAssignments,
} from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
import { useAuth } from "@/lib/auth";
import AdminShell from "./AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const FILTER_PILLS = [
  { value: "all", label: "All students" },
  { value: "unassigned", label: "Unassigned" },
  { value: "assigned", label: "Assigned" },
  { value: "needs_reassignment", label: "Needs Reassignment" },
];

export default function AdminAssignments() {
  const { user } = useAuth();
  const isSuperAdmin = user?.permission_role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const [organizations, setOrganizations] = useState([]);
  // FP3-053 — Initial orgId honours the sidebar context selector
  // (URL `?as_org=`). The local "Organization" picker on this page
  // is now a soft control: it sets `?as_org=` (so the sidebar stays
  // in sync) and the same URL param drives the data fetch. When the
  // sidebar selector changes, this page picks it up.
  const urlAsOrg = (searchParams.get("as_org") || "").trim();
  const urlAsSchool = (searchParams.get("as_school") || "").trim();
  const [orgId, setOrgId] = useState(
    urlAsOrg || (isSuperAdmin ? "" : user?.organization_id || "")
  );
  // Re-sync if the sidebar selector flips the URL.
  useEffect(() => {
    if (isSuperAdmin) {
      setOrgId(urlAsOrg || "");
    }
  }, [urlAsOrg, isSuperAdmin]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState(
    searchParams.get("state") || "all"
  );
  const [schoolFilter, setSchoolFilter] = useState(
    searchParams.get("school") || ""
  );
  const [serviceAreaFilter, setServiceAreaFilter] = useState("");

  useEffect(() => {
    if (isSuperAdmin) {
      api
        .getOrganizations()
        .then((r) => setOrganizations(r.data || []))
        .catch(() => setOrganizations([]));
    }
  }, [isSuperAdmin]);

  // FP3-119B — React Query manages the assignments cache. Filter
  // values participate in the cache key so switching filters
  // returns from cache immediately when previously visited.
  const enabled = !!(orgId || !isSuperAdmin);
  const queryParams = useMemo(() => ({
    organization_id: orgId || undefined,
    // FP3-053 — Honour the sidebar context selector's school pin
    // in addition to the local school filter chip.
    school_id: schoolFilter || urlAsSchool || undefined,
    service_area: serviceAreaFilter || undefined,
    assignment_state: stateFilter !== "all" ? stateFilter : undefined,
    search: search || undefined,
  }), [orgId, schoolFilter, urlAsSchool, serviceAreaFilter, stateFilter, search]);
  const assignmentsQuery = useStudentAssignments(enabled ? queryParams : undefined);
  const data = assignmentsQuery.data || {
    students: [],
    schools: [],
    providers: [],
    total_students: 0,
    unassigned_students: 0,
  };
  const refetching = assignmentsQuery.isFetching && !assignmentsQuery.isPending;
  const dataUpdatedAt = assignmentsQuery.dataUpdatedAt;

  // Surface fetch errors as toasts (parity with the previous
  // useEffect-based loader).
  useEffect(() => {
    if (assignmentsQuery.isError) {
      const err = assignmentsQuery.error;
      toast.error(
        err?.response?.data?.detail || "Could not load student assignments"
      );
    }
  }, [assignmentsQuery.isError, assignmentsQuery.error]);

  const load = useCallback(() => {
    invalidateStudentAssignments();
  }, []);

  useEffect(() => {
    if (stateFilter && stateFilter !== "all") {
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.set("state", stateFilter);
        return next;
      });
    } else {
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.delete("state");
        return next;
      });
    }
  }, [stateFilter, setSearchParams]);

  const providers = data.providers || [];

  const handleAssign = async (student, providerId, serviceArea) => {
    if (!providerId) return;
    try {
      await api.createAssignment({
        student_id: student.id,
        provider_id: providerId,
        organization_id: data.organization_id || orgId,
        school_id: student.school_id || "",
        service_area: serviceArea || "",
      });
      toast.success("Provider assigned");
      // FP3-119B — Provider assignment mutation invalidates the
      // student/provider/assignment/reports caches.
      invalidateAfterMutation.providerAssignmentChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not assign provider"
      );
    }
  };

  const handleRemove = async (student, providerId) => {
    try {
      // FP3-104 — canonical pair-based removal (never blocked by a
      // missing SPA mirror row on legacy/drifted assignments).
      await api.removeAssignment({
        student_id: student.id,
        provider_id: providerId,
      });
      toast.success("Provider removed");
      invalidateAfterMutation.providerAssignmentChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not remove provider"
      );
    }
  };

  // FP3-029B5 — Set / update / clear a student's school. Surfaces the
  // 409 orphaned-provider error with a friendly toast prompting the
  // admin to reassign or force.
  const handleSetStudentSchool = async (student, schoolId, { force = false } = {}) => {
    try {
      const r = await api.updateStudentSchool(student.id, {
        school_id: schoolId,
        force,
      });
      toast.success(
        schoolId
          ? `School set: ${r.data?.school_name || ""}`
          : "School cleared"
      );
      // FP3-119B — Assignment change affects student/provider/assignment
      // caches + reports (assigned minutes aggregates).
      invalidateAfterMutation.providerAssignmentChanged();
      return { ok: true, data: r.data };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object") {
        // Pass the orphan-warning back to the row so it can render
        // an explicit "Reassign providers or force" follow-up UI.
        return { ok: false, conflict: detail };
      }
      toast.error(
        (typeof detail === "string" && detail) || "Could not update school"
      );
      return { ok: false };
    }
  };

  return (
    <AdminShell
      title="Student Assignments"
      subtitle="Assign or reassign students to providers within this organization."
    >
      {/* Super admin org selector */}
      {isSuperAdmin && (
        <Card className="border-[#E5E3DB] rounded-2xl mb-4">
          <CardContent className="p-4 flex items-center gap-3">
            <Buildings size={16} weight="duotone" className="text-[#3F4756]" />
            <select
              data-testid="assignments-org-select"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="h-9 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
            >
              <option value="">— Select organization —</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-[#6B7280]">
              Super admin must pick an organization to manage assignments.
            </span>
          </CardContent>
        </Card>
      )}

      {/* FP3-029B2 — Provider · School assignments moved to the
          Providers page where it belongs. Keep a calm one-line
          pointer here so admins know where to manage it. */}
      {(orgId || !isSuperAdmin) && (
        <Card
          data-testid="provider-school-relocated"
          className="border-[#E5E3DB] rounded-2xl mb-4"
        >
          <CardContent className="p-3 flex items-center justify-between">
            <span className="text-[12.5px] text-[#6B7280] flex items-center gap-2">
              <Buildings size={14} weight="duotone" className="text-[#3F4756]" />
              Provider school access is managed on the Providers page.
            </span>
            <a
              data-testid="provider-school-link"
              href="/admin/providers"
              className="text-[12px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
            >
              Open Providers →
            </a>
          </CardContent>
        </Card>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Tile
          label="Students in scope"
          value={data.total_students || 0}
          tone="default"
          testId="tile-total-students"
        />
        <Tile
          label="Unassigned"
          value={data.unassigned_students || 0}
          tone={data.unassigned_students > 0 ? "warn" : "ok"}
          testId="tile-unassigned-students"
        />
        <Tile
          label="Needs Reassignment"
          value={data.needs_reassignment_students || 0}
          tone={data.needs_reassignment_students > 0 ? "danger" : "ok"}
          testId="tile-needs-reassignment-students"
        />
        <Tile
          label="Providers in scope"
          value={providers.length}
          tone="default"
          testId="tile-providers"
        />
        <Tile
          label="Schools"
          value={(data.schools || []).length}
          tone="default"
          testId="tile-schools"
        />
      </div>

      {/* Filters row */}
      <Card className="border-[#E5E3DB] rounded-2xl mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            {FILTER_PILLS.map((p) => (
              <button
                key={p.value}
                data-testid={`filter-state-${p.value}`}
                onClick={() => setStateFilter(p.value)}
                className={`text-[11.5px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                  stateFilter === p.value
                    ? "bg-[#1E3F33] text-white"
                    : "bg-[#F5F4ED] text-[#6B7280] hover:bg-[#EDEAE0]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <select
            data-testid="filter-school"
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            className="h-9 border border-[#E5E3DB] rounded-md px-2 text-[12.5px] bg-white"
          >
            <option value="">All schools</option>
            {(data.schools || []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <Input
            data-testid="filter-service-area"
            placeholder="Filter by service area"
            value={serviceAreaFilter}
            onChange={(e) => setServiceAreaFilter(e.target.value)}
            className="h-9 w-44 border-[#E5E3DB] text-[12.5px]"
          />
          <Input
            data-testid="filter-search"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 flex-1 min-w-[180px] border-[#E5E3DB] text-[12.5px]"
          />
          <div className="ml-auto">
            <RefreshChip
              dataUpdatedAt={dataUpdatedAt}
              isFetching={refetching}
              onRefresh={() => assignmentsQuery.refetch()}
              testid="assignments-refresh"
            />
          </div>
        </CardContent>
      </Card>

      {/* Student table */}
      {!orgId && isSuperAdmin && (
        <Card className="border-[#E5E3DB] rounded-2xl">
          <CardContent
            data-testid="assignments-pick-org"
            className="p-6 text-center text-[12.5px] text-[#6B7280]"
          >
            Choose an organization to view its student assignments.
          </CardContent>
        </Card>
      )}

      {(orgId || !isSuperAdmin) && (
        <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-[12px]" data-testid="assignments-table">
              <thead>
                <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                  <th className="text-left px-4 py-2.5">Student</th>
                  <th className="text-left px-4 py-2.5">Grade</th>
                  <th className="text-left px-4 py-2.5">School / Campus</th>
                  <th className="text-left px-4 py-2.5">Current providers</th>
                  <th className="text-left px-4 py-2.5">Past providers</th>
                  <th className="text-right px-4 py-2.5">Assign</th>
                </tr>
              </thead>
              <tbody>
                {data.students?.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-[#6B7280]"
                    >
                      No students match the current filters.
                    </td>
                  </tr>
                )}
                {data.students.map((s) => (
                  <StudentRow
                    key={s.id}
                    student={s}
                    providers={providers}
                    schools={data.schools || []}
                    onAssign={handleAssign}
                    onRemove={handleRemove}
                    onSetSchool={handleSetStudentSchool}
                  />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </AdminShell>
  );
}

function Tile({ label, value, tone, testId }) {
  const tones = {
    ok: "bg-[#E8F0EB] text-[#1E3F33]",
    warn: "bg-[#F5EFE3] text-[#7A6A2C]",
    danger: "bg-[#F8EFEA] text-[#8A2E1E]",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-md px-3 py-3 ${tones[tone] || "bg-[#F5F4ED] text-[#1F2937]"}`}
    >
      <div className="text-[10.5px] uppercase tracking-[0.1em] font-semibold opacity-80">
        {label}
      </div>
      <div className="text-[18px] font-semibold heading-font tracking-tight">
        {value ?? 0}
      </div>
    </div>
  );
}

function StudentRow({ student, providers, schools, onAssign, onRemove, onSetSchool }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedProvider, setPickedProvider] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [schoolPickerOpen, setSchoolPickerOpen] = useState(false);
  const [pickedSchool, setPickedSchool] = useState(student.school_id || "");
  const [savingSchool, setSavingSchool] = useState(false);
  const [orphanWarning, setOrphanWarning] = useState(null);
  const submitDisabled = !pickedProvider;

  const submit = async () => {
    await onAssign(student, pickedProvider, serviceArea);
    setPickerOpen(false);
    setPickedProvider("");
    setServiceArea("");
  };

  const submitSchool = async ({ force = false } = {}) => {
    setSavingSchool(true);
    const r = await onSetSchool(student, pickedSchool, { force });
    setSavingSchool(false);
    if (r.ok) {
      setSchoolPickerOpen(false);
      setOrphanWarning(null);
    } else if (r.conflict) {
      setOrphanWarning(r.conflict);
    }
  };

  // FP3-093B — current (active/monitoring/legacy) vs past
  // (graduated/archived) relationships. The backend splits them; the
  // client-side fallback keeps older responses working.
  const allAssigns = student.assignments || [];
  const isEnded = (a) =>
    ["graduated", "archived"].includes((a.status || "active").toLowerCase());
  const assigns =
    student.current_assignments || allAssigns.filter((a) => !isEnded(a));
  const pastAssigns =
    student.past_assignments || allAssigns.filter(isEnded);
  const schoolName =
    student.campus ||
    (schools || []).find((s) => s.id === student.school_id)?.name ||
    "";

  return (
    <tr
      data-testid={`assignment-row-${student.id}`}
      className="border-b border-[#F8F7F2] last:border-b-0 align-top"
    >
      <td className="px-4 py-3 text-[#1F2937]">
        <div className="flex items-center gap-1.5">
          <GraduationCap size={13} weight="duotone" className="text-[#3F4756]" />
          <span className="font-medium">
            {student.first_name} {student.last_name}
          </span>
        </div>
        {student.external_id && (
          <div className="text-[10px] text-[#9AA0A6] mt-0.5 font-mono">
            {student.external_id}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-[#6B7280]">{student.grade || "—"}</td>
      <td className="px-4 py-3 align-top">
        {!schoolPickerOpen && (
          <div className="flex items-center gap-1.5">
            {schoolName ? (
              <span
                data-testid={`assignment-${student.id}-school-chip`}
                className="inline-flex items-center gap-1 text-[11px] text-[#1E3F33] bg-[#E8F0EB] px-2 py-0.5 rounded-full"
              >
                <Buildings size={10} weight="duotone" /> {schoolName}
              </span>
            ) : (
              <span
                data-testid={`assignment-${student.id}-school-missing`}
                className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#F5EFE3", color: "#7A6A2C" }}
              >
                <WarningCircle size={10} weight="duotone" /> No school
              </span>
            )}
            <button
              data-testid={`assignment-${student.id}-school-edit`}
              onClick={() => {
                setPickedSchool(student.school_id || "");
                setSchoolPickerOpen(true);
                setOrphanWarning(null);
              }}
              className="text-[10.5px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
            >
              {schoolName ? "Change" : "Assign school"}
            </button>
          </div>
        )}
        {schoolPickerOpen && (
          <div className="flex flex-col gap-1.5">
            <select
              data-testid={`assignment-${student.id}-school-select`}
              value={pickedSchool}
              onChange={(e) => {
                setPickedSchool(e.target.value);
                setOrphanWarning(null);
              }}
              className="h-7 border border-[#E5E3DB] rounded-md px-2 text-[12px] bg-white min-w-[180px]"
            >
              <option value="">— No school —</option>
              {(schools || []).map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name}
                </option>
              ))}
            </select>
            {orphanWarning && (
              <div
                data-testid={`assignment-${student.id}-school-orphan-warning`}
                className="text-[10.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E5C9C2] rounded-md p-2 leading-snug"
              >
                <strong>Heads up.</strong>{" "}
                {orphanWarning.message}
                <ul className="mt-1 list-disc list-inside">
                  {(orphanWarning.orphaned_providers || []).map((p) => (
                    <li key={p.provider_id}>{p.name}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-1.5">
              <Button
                data-testid={`assignment-${student.id}-school-cancel`}
                size="sm"
                variant="outline"
                onClick={() => {
                  setSchoolPickerOpen(false);
                  setOrphanWarning(null);
                }}
                className="h-7 text-[11.5px] border-[#E5E3DB] text-[#6B7280]"
              >
                Cancel
              </Button>
              <Button
                data-testid={`assignment-${student.id}-school-submit`}
                size="sm"
                onClick={() => submitSchool({ force: false })}
                disabled={savingSchool}
                className="h-7 text-[11.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
              >
                Save
              </Button>
              {orphanWarning && (
                <Button
                  data-testid={`assignment-${student.id}-school-force`}
                  size="sm"
                  variant="outline"
                  onClick={() => submitSchool({ force: true })}
                  disabled={savingSchool}
                  className="h-7 text-[11.5px] border-[#C5A06C] text-[#7A6A2C] hover:bg-[#F5EFE3]"
                >
                  Save anyway
                </Button>
              )}
            </div>
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {assigns.length === 0 && (
          <span
            data-testid={`assignment-${student.id}-unassigned`}
            className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
            style={{ backgroundColor: "#F5EFE3", color: "#7A6A2C" }}
          >
            <WarningCircle size={10} weight="duotone" /> Unassigned
          </span>
        )}
        {student.needs_reassignment && (
          <span
            data-testid={`assignment-${student.id}-needs-reassignment`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] font-semibold px-2 py-0.5 rounded-full bg-[#F8EFEA] text-[#8A2E1E] border border-[#E3C4BE] mb-1"
          >
            <WarningCircle size={10} weight="duotone" /> Reassignment needed
          </span>
        )}
        <div className="flex flex-wrap gap-1.5">
          {assigns.map((a) => {
            const disabled = a.account_disabled;
            const supDisabled = a.assistant_assignment && a.supervisor_account_disabled;
            const cls = disabled
              ? "bg-[#F8EFEA] text-[#8A2E1E] border border-[#E3C4BE]"
              : supDisabled
                ? "bg-[#FDF8F1] text-[#8A5A1E] border border-[#EAD8BE]"
                : "bg-[#E8F0EB] text-[#1E3F33]";
            return (
              <span
                key={a.provider_id || a.id}
                data-testid={`assignment-${student.id}-chip-${a.provider_id}`}
                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${cls}`}
              >
                <Users size={10} weight="duotone" />
                {a.provider_first_name || ""} {a.provider_last_name || ""}
                {(a.status || "active") === "monitoring" && !disabled && (
                  <span className="text-[9.5px] font-semibold uppercase tracking-wide text-[#3F6E5A]">
                    · monitoring
                  </span>
                )}
                {disabled && (
                  <span
                    data-testid={`assignment-${student.id}-chip-${a.provider_id}-disabled`}
                    className="text-[9.5px] font-semibold uppercase tracking-wide"
                  >
                    · Account disabled
                  </span>
                )}
                {supDisabled && (
                  <span className="text-[9.5px] font-semibold uppercase tracking-wide">
                    · Supervisor review
                  </span>
                )}
                {a.service_area && (
                  <span className="text-[10px] text-[#3F4756]/70 ml-1">
                    · {a.service_area}
                  </span>
                )}
                <button
                  data-testid={`assignment-${student.id}-remove-${a.provider_id}`}
                  onClick={() => onRemove(student, a.provider_id)}
                  className="ml-1 text-current/60 hover:text-[#7A3A2C]"
                  title="Remove provider"
                >
                  <X size={9} />
                </button>
              </span>
            );
          })}
        </div>
      </td>
      {/* FP3-093B — PAST providers: graduated/archived relationships
          stay visible as muted history. No inline X — the Team tab is
          the deliberate place to reactivate, mark reviewed, or remove
          (with the strong history warning). */}
      <td className="px-4 py-3">
        {pastAssigns.length === 0 ? (
          <span className="text-[11px] text-[#9B9890]">—</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {pastAssigns.map((a) => {
              const st = (a.status || "").toLowerCase();
              const cls = st === "graduated"
                ? "bg-[#F5EDDD] text-[#8A6B33] border border-[#E3CD9C]"
                : "bg-[#F2ECEA] text-[#8C5F5A] border border-[#D8BCB6]";
              return (
                <span
                  key={a.provider_id || a.id}
                  data-testid={`assignment-${student.id}-past-chip-${a.provider_id}`}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${cls}`}
                >
                  {a.provider_first_name || ""} {a.provider_last_name || ""}
                  <span className="text-[10px] font-semibold uppercase tracking-wide ml-0.5">
                    · {st}
                  </span>
                  {a.assistant_assignment && a.supervising_provider_name && (
                    <span
                      data-testid={`assignment-${student.id}-past-chip-${a.provider_id}-assistant-note`}
                      className="text-[10px] ml-0.5"
                    >
                      · Assistant under {a.supervising_provider_name}
                    </span>
                  )}
                </span>
              );
            })}
            <Link
              to={`/admin/students/${student.id}?tab=team`}
              data-testid={`assignment-${student.id}-review-team`}
              className="text-[10.5px] font-medium text-[#1E3F33] underline underline-offset-2 hover:text-[#173127]"
              title="Reactivate, mark reviewed, or remove this historical relationship on the Team tab"
            >
              Review in Team
            </Link>
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {!pickerOpen && (
          <Button
            data-testid={`assignment-${student.id}-open-picker`}
            size="sm"
            variant="outline"
            onClick={() => setPickerOpen(true)}
            className="h-7 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            <Plus size={11} className="mr-1" />
            Assign provider
          </Button>
        )}
        {pickerOpen && (
          <div className="inline-flex flex-col items-end gap-1.5">
            <select
              data-testid={`assignment-${student.id}-provider-select`}
              value={pickedProvider}
              onChange={(e) => setPickedProvider(e.target.value)}
              className="h-7 border border-[#E5E3DB] rounded-md px-2 text-[12px] bg-white min-w-[180px]"
            >
              <option value="">— Provider —</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                  {p.role ? ` · ${p.role}` : ""}
                </option>
              ))}
            </select>
            <Input
              data-testid={`assignment-${student.id}-service-area-input`}
              placeholder="Service area (optional)"
              value={serviceArea}
              onChange={(e) => setServiceArea(e.target.value)}
              className="h-7 text-[11.5px] border-[#E5E3DB] min-w-[180px]"
            />
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPickerOpen(false)}
                className="h-7 text-[11.5px] border-[#E5E3DB] text-[#6B7280]"
              >
                Cancel
              </Button>
              <Button
                data-testid={`assignment-${student.id}-submit`}
                size="sm"
                onClick={submit}
                disabled={submitDisabled}
                className="h-7 text-[11.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
              >
                Assign
              </Button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}


